import { startTransition, StrictMode } from 'react';

import { hydrateRoot } from 'react-dom/client';

import { HydratedRouter } from 'react-router/dom';

import { I18nProvider } from '@lingui/react';
import { detect, fromHtmlTag } from '@lingui/detect-locale';

import * as Sentry from '@sentry/react';

import { i18n, loadCatalog } from '~/modules/lingui/lingui';
import { createClientLogger } from '~/utils/logger.client';

// Only initialize Sentry if explicitly enabled or not set
const isSentryEnabled = window.ENV?.SENTRY_ENABLED !== 'false';

// Initialize logger inside a function to ensure client-side only
function initializeClientSide() {
  const logger = createClientLogger('ClientEntry');

  if (window.ENV) {
    logger.info('Initializing with ENV', { metadata: window.ENV });
  }

  if (isSentryEnabled) {
    logger.info('Initializing Sentry');
    Sentry.init({
      dsn: 'https://80649b0a19915dc49866425497a26a48@o4509820848635904.ingest.us.sentry.io/4509820850012160',
      tracesSampleRate: 1,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
    });
  }

  function clearBrowserExtensionInjectionsBeforeHydration() {
    logger.info('Clearing browser extensions');

    // 1. Remove extension-injected elements from the entire document
    document
      .querySelectorAll(
        [
          // Direct children of <html> that aren't <head> or <body>
          'html > *:not(body, head)',
          // Extension scripts and stylesheets
          'script[src*="extension://"]',
          'link[href*="extension://"]',
          'style[data-vite-dev-id*="extension://"]',
          // Grammarly
          'grammarly-desktop-integration',
          '[data-grammarly-shadow-root]',
          'grammarly-extension',
          // LastPass
          '[data-lastpass-icon-root]',
          '[data-lastpass-root]',
          // 1Password
          '[data-onepassword-extension]',
          // Dark Reader
          'style.darkreader',
          'meta[name="darkreader"]',
          // Generic: any element with extension:// in any attribute value
          '[style*="extension://"]',
        ].join(', ')
      )
      .forEach(s => {
        s.parentNode?.removeChild(s);
      });

    // 2. Remove extension-injected styles from <head> (styles not from our app)
    document.head.querySelectorAll('style').forEach(style => {
      // Keep Vite HMR styles and our own styles
      if (
        style.hasAttribute('data-vite-dev-id') ||
        style.hasAttribute('data-emotion') ||
        style.parentElement?.tagName === 'HEAD'
      ) {
        // Check if it looks like an extension style
        const text = style.textContent || '';
        if (text.includes('extension://') || text.includes('chrome-extension://') || text.includes('moz-extension://')) {
          style.remove();
        }
      }
    });

    // 3. Clean attributes added by extensions on key elements
    const $targets = {
      html: {
        $elm: document.querySelector('html')!,
        allowedAttributes: ['lang', 'dir', 'class', 'style'],
      },
      head: {
        $elm: document.querySelector('head')!,
        allowedAttributes: [''],
      },
      body: {
        $elm: document.querySelector('body')!,
        allowedAttributes: ['class', 'style'],
      },
    };

    Object.entries($targets).forEach(([, target]) => {
      target.$elm.getAttributeNames().forEach(attr => {
        if (!target.allowedAttributes.includes(attr)) {
          target.$elm.removeAttribute(attr);
        }
      });
    });
  }

  async function hydrate() {
    const t0 = performance.now();
    logger.info('Starting hydration', {
      metadata: {
        inlineCatalogExists: !!window.__LINGUI_CATALOG__,
        inlineCatalogLocale: window.__LINGUI_CATALOG__?.locale ?? 'none',
        inlineCatalogMessageCount: window.__LINGUI_CATALOG__?.messages
          ? Object.keys(window.__LINGUI_CATALOG__.messages).length
          : 0,
        stylesheetsLoaded: document.styleSheets.length,
        htmlLang: document.documentElement.lang,
      },
    });

    clearBrowserExtensionInjectionsBeforeHydration();

    // Detect locale from html lang attribute (set by server)
    const locale = detect(fromHtmlTag("lang")) || "en";
    logger.info('Locale detected', { metadata: { locale } });

    // Use inlined catalog from SSR if available (synchronous, prevents FOUC)
    if (window.__LINGUI_CATALOG__?.locale === locale) {
      i18n.load(locale, window.__LINGUI_CATALOG__.messages);
      i18n.activate(locale);
      logger.info('Catalog loaded SYNC from inline', { metadata: { locale, ms: Math.round(performance.now() - t0) } });
    } else {
      logger.info('Catalog falling back to ASYNC import', {
        metadata: { locale, catalogLocale: window.__LINGUI_CATALOG__?.locale ?? 'none' },
      });
      await loadCatalog(locale);
      logger.info('Catalog loaded ASYNC', { metadata: { locale, ms: Math.round(performance.now() - t0) } });
    }

    logger.info('Hydrating React', { metadata: { ms: Math.round(performance.now() - t0), stylesheetsLoaded: document.styleSheets.length } });
    startTransition(() => {
      hydrateRoot(
        document,
        <StrictMode>
          <I18nProvider i18n={i18n}>
            <HydratedRouter />
          </I18nProvider>
        </StrictMode>,
        {
          onRecoverableError(error) {
            // Suppress hydration mismatch errors caused by browser extensions
            const msg = String(error);
            if (msg.includes('Hydration') || msg.includes('hydrat')) {
              logger.info('Suppressed hydration mismatch (likely browser extension)', { metadata: { error: msg.slice(0, 200) } });
              return;
            }
            console.error(error);
          },
        }
      );
    });
    logger.info('Hydration queued', { metadata: { ms: Math.round(performance.now() - t0) } });

  }

  // Hydrate immediately to prevent FOUC (Flash of Unstyled Content)
  hydrate();
}

// Only run initialization if we're in the browser
if (typeof window !== 'undefined') {
  initializeClientSide();
}
